"use client";

import { useLocale, useTranslations } from "next-intl";
import type React from "react";
import { useMemo } from "react";
import { useSidebar } from "@/context/sidebar";
import { usePathname } from "@/i18n/navigation";
import { getDirection, type Locale } from "@/lib/i18n";
import { cn } from "@/lib/utils";
import { isNavDestinationActive, NAV_DESTINATIONS } from "../nav-destinations";
import { BottomNavItem } from "./BottomNavItem";
import { BottomNavNotch } from "./BottomNavNotch";

// Horizontal inset of the item row, as a fraction of the nav width. MUST stay
// in sync with the `px-[8.4%]` class below: both place the equal-width items at
// the same predictable fractional centers so the notch dip lands exactly under
// the active item at every width and in both LTR and RTL.
const EDGE_PAD = 0.084;
const VIEWBOX_WIDTH = 428;

interface BottomNavProps {
  className?: string;
  initialPath?: string;
}

export const BottomNav: React.FC<BottomNavProps> = ({
  className,
  initialPath,
}) => {
  const t = useTranslations("nav");
  const isMenuOpen = useSidebar((state) => state.isOpen);
  const pathname = usePathname();
  const isRtl = getDirection(useLocale() as Locale) === "rtl";

  // Use server pathname for initial render, then switch to client pathname when available
  const currentPath = useMemo(() => {
    return pathname || initialPath || "";
  }, [pathname, initialPath]);

  // The active destination index. When the current path matches no destination
  // (e.g. a missing page), fall back to Home (0). The notch dip AND the
  // highlighted item both read this single source, so they always agree —
  // previously the notch fell back to Home while no item was highlighted, so the
  // dip sat under an inactive Home on every non-nav page.
  const activeIndex = useMemo(() => {
    const i = NAV_DESTINATIONS.findIndex((item) =>
      isNavDestinationActive(currentPath, item.href),
    );
    return i === -1 ? 0 : i;
  }, [currentPath]);

  // Center of the active item in the notch SVG's 428-wide viewBox. Items are
  // equal-width (flex-1) inside an `EDGE_PAD` inset, so each item center sits at
  // a fixed fraction of the width; computing it directly (instead of a fixed
  // per-item path or an SVG mirror) keeps the dip under the active item in both
  // writing directions and at any width.
  const dipCenter = useMemo(() => {
    const n = NAV_DESTINATIONS.length;
    const visualIndex = isRtl ? n - 1 - activeIndex : activeIndex;
    const fraction =
      EDGE_PAD + (1 - 2 * EDGE_PAD) * ((2 * visualIndex + 1) / (2 * n));
    return fraction * VIEWBOX_WIDTH;
  }, [activeIndex, isRtl]);

  if (isMenuOpen) return null;

  return (
    <nav
      className={cn(
        // Mobile/tablet-only: desktop navigation lives in the TopNav (lg+).
        "lg:hidden",
        "fixed inset-x-0 bottom-0 z-100 mx-auto",
        "w-full max-w-107",
        "flex items-center justify-center",
        className,
      )}
    >
      <BottomNavNotch dipCenter={dipCenter} />

      <div
        className={cn(
          "relative z-20 w-full",
          "flex items-end justify-around gap-0",
          // Fractional padding (must match EDGE_PAD) keeps the item centers at
          // width-independent fractions so they align with the notch dip.
          "px-[8.4%]",
        )}
      >
        {NAV_DESTINATIONS.map((item, index) => {
          return (
            <BottomNavItem
              key={item.href}
              href={item.href}
              icon={item.icon}
              label={t(item.labelKey)}
              isActive={index === activeIndex}
            />
          );
        })}
      </div>
    </nav>
  );
};
